import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageLoader } from "../../components/PageLoader.tsx";
import {
  IconDocument,
  IconHourglass,
  IconSearch,
  IconCheck,
  IconX,
  IconClock,
  IconBan,
  IconQuestion,
  IconCalendar,
  IconAlertTriangle,
  IconReceipt,
  IconChevronRight,
} from "../../components/Icons.tsx";
import { haptic } from "../../lib/telegram.ts";
import { useOrders } from "@/features/orders/hooks";
import { useOnRestoreFromBackground } from "../../lib/use-lifecycle.ts";
import { getCurrencySymbol } from "../../lib/format.ts";

const CARD_CLASS =
  "rounded-2xl overflow-hidden bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50";

// Order type for this page
interface OrderListItem {
  id: string;
  status: string;
  totalAmount: string;
  currency: string;
  createdAt: string;
  event: {
    id: string;
    title: string;
    startsAt: string;
    imageUrl?: string | null;
  };
  items: Array<{
    quantity: number;
    ticketType: {
      name: string;
    };
  }>;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatShortDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
  });
}

// Status configuration
const STATUS_CONFIG: Record<
  string,
  { label: string; color: string; bgColor: string; icon: React.ReactNode }
> = {
  CREATED: {
    label: "Создан",
    color: "text-blue-600",
    bgColor: "bg-blue-100",
    icon: <IconDocument className="w-4 h-4" />,
  },
  AWAITING_PAYMENT: {
    label: "Ожидает оплаты",
    color: "text-amber-600",
    bgColor: "bg-amber-100",
    icon: <IconHourglass className="w-4 h-4" />,
  },
  PENDING_VERIFICATION: {
    label: "На проверке",
    color: "text-purple-600",
    bgColor: "bg-purple-100",
    icon: <IconSearch className="w-4 h-4" />,
  },
  VERIFIED: {
    label: "Подтверждён",
    color: "text-green-600",
    bgColor: "bg-green-100",
    icon: <IconCheck className="w-4 h-4" />,
  },
  TICKET_ISSUED: {
    label: "Билеты выданы",
    color: "text-green-600",
    bgColor: "bg-green-100",
    icon: <IconCheck className="w-4 h-4" />,
  },
  CANCELLED: {
    label: "Отменён",
    color: "text-slate-500",
    bgColor: "bg-slate-100",
    icon: <IconX className="w-4 h-4" />,
  },
  EXPIRED: {
    label: "Истёк",
    color: "text-red-600",
    bgColor: "bg-red-100",
    icon: <IconClock className="w-4 h-4" />,
  },
  REJECTED: {
    label: "Отклонён",
    color: "text-red-600",
    bgColor: "bg-red-100",
    icon: <IconBan className="w-4 h-4" />,
  },
};

function getStatusConfig(status: string) {
  return (
    STATUS_CONFIG[status] ?? {
      label: status,
      color: "text-slate-600",
      bgColor: "bg-slate-100",
      icon: <IconQuestion className="w-4 h-4" />,
    }
  );
}

type OrdersFilter = "all" | "active" | "completed" | "archive";

/** Контент списка заказов без Layout (для встраивания в страницу «Заказы и билеты»). */
export function OrdersContent({ eventIdFilter }: { eventIdFilter?: string }) {
  const navigate = useNavigate();

  // URQL hook for orders
  const {
    orders,
    isLoading,
    error: ordersError,
    refetch,
  } = useOrders();

  const error = ordersError?.message ?? null;

  const [filter, setFilter] = useState<OrdersFilter>("all");

  // Refetch on restore from background
  useOnRestoreFromBackground(() => {
    refetch();
  });

  const handleOrderClick = (order: OrderListItem) => {
    haptic("selection");

    // Navigate based on status
    if (order.status === "CREATED") {
      navigate(`/order/${order.id}`);
    } else if (
      order.status === "AWAITING_PAYMENT" ||
      order.status === "PENDING_VERIFICATION"
    ) {
      navigate(`/payment/${order.id}`);
    } else {
      navigate(`/order/${order.id}`);
    }
  };

  if (isLoading) {
    return <PageLoader message="Загрузка..." />;
  }

  if (error) {
    return (
      <div className={`${CARD_CLASS} p-5 text-center`}>
        <IconAlertTriangle className="w-10 h-10 xs:w-12 xs:h-12 md-mobile:w-14 md-mobile:h-14 lg-mobile:w-16 lg-mobile:h-16 mx-auto mb-3 xs:mb-4 lg-mobile:mb-5 text-amber-500" />
        <h2 className="text-lg xs:text-xl md-mobile:text-2xl lg-mobile:text-[1.75rem] font-bold text-slate-900 mb-2 lg-mobile:mb-3">
          Ошибка загрузки
        </h2>
        <p className="text-slate-600 mb-4 xs:mb-6 lg-mobile:mb-8 text-sm md-mobile:text-base lg-mobile:text-lg">
          {error}
        </p>
        <button onClick={() => refetch()} className="btn-primary w-full">
          Попробовать снова
        </button>
      </div>
    );
  }

  const filteredOrders = eventIdFilter
    ? orders.filter((o) => o.event.id === eventIdFilter)
    : orders;

  if (filteredOrders.length === 0) {
    return (
      <div className={`${CARD_CLASS} p-5 text-center`}>
        <IconReceipt className="w-12 h-12 xs:w-14 xs:h-14 md-mobile:w-16 md-mobile:h-16 lg-mobile:w-20 lg-mobile:h-20 mx-auto mb-3 xs:mb-4 lg-mobile:mb-5 text-slate-300" />
        <h2 className="text-lg xs:text-xl md-mobile:text-2xl lg-mobile:text-[1.75rem] font-bold text-slate-900 mb-2 lg-mobile:mb-3">
          Пока пусто
        </h2>
        <p className="text-slate-600 text-xs xs:text-sm md-mobile:text-base lg-mobile:text-lg mb-4 xs:mb-6 lg-mobile:mb-8">
          {eventIdFilter ? (
            "Нет заказов по этому мероприятию."
          ) : (
            <>
              У вас пока нет заказов.
              <br />
              Выберите мероприятие и купите билеты!
            </>
          )}
        </p>
        <button onClick={() => navigate("/")} className="btn-primary w-full">
          Посмотреть мероприятия
        </button>
      </div>
    );
  }

  // Group orders by status
  const activeOrders = filteredOrders.filter((o) =>
    ["CREATED", "AWAITING_PAYMENT", "PENDING_VERIFICATION"].includes(o.status),
  );
  const completedOrders = filteredOrders.filter((o) =>
    ["COMPLETED", "TICKET_ISSUED"].includes(o.status),
  );
  const otherOrders = filteredOrders.filter((o) =>
    ["CANCELLED", "EXPIRED", "REJECTED"].includes(o.status),
  );

  const filterTabs: { value: OrdersFilter; label: string; count: number }[] = [
    { value: "all", label: "Все", count: filteredOrders.length },
    { value: "active", label: "Активные", count: activeOrders.length },
    { value: "completed", label: "Оплаченные", count: completedOrders.length },
    { value: "archive", label: "Архив", count: otherOrders.length },
  ];

  const showActive = filter === "all" || filter === "active";
  const showCompleted = filter === "all" || filter === "completed";
  const showArchive = filter === "all" || filter === "archive";

  const isEmptyFiltered =
    (filter === "active" && activeOrders.length === 0) ||
    (filter === "completed" && completedOrders.length === 0) ||
    (filter === "archive" && otherOrders.length === 0);

  const OrderCard = ({ order }: { order: OrderListItem }) => {
    const statusConfig = getStatusConfig(order.status);
    const totalQuantity = order.items.reduce(
      (sum, item) => sum + item.quantity,
      0,
    );
    const posterUrl = order.event.imageUrl ?? null;

    return (
      <button
        type="button"
        onClick={() => handleOrderClick(order)}
        className="w-full flex items-center gap-3 p-3 min-h-[72px] text-left rounded-none first:rounded-t-xl last:rounded-b-xl border-b border-slate-100 last:border-b-0 bg-white active:bg-slate-50 [-webkit-tap-highlight-color:transparent]"
      >
        {/* Миниатюра афиши — нативный список с картинкой слева */}
        <div className="w-14 h-14 rounded-xl overflow-hidden bg-slate-100 shrink-0 flex items-center justify-center">
          {posterUrl ? (
            <img
              src={posterUrl}
              alt=""
              className="w-full h-full object-cover"
            />
          ) : (
            <IconCalendar className="w-6 h-6 text-slate-400" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-[15px] text-slate-900 truncate">
              {order.event.title}
            </span>
            <span
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium shrink-0 ${statusConfig.bgColor} ${statusConfig.color}`}
            >
              {statusConfig.icon}
              {statusConfig.label}
            </span>
          </div>
          <p className="text-[13px] text-slate-500 mt-0.5 flex items-center gap-1 truncate">
            <IconCalendar className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            {formatDate(order.event.startsAt)}
          </p>
          <p className="text-[12px] text-slate-400 mt-0.5">
            {totalQuantity}{" "}
            {totalQuantity === 1 ? "билет" : totalQuantity < 5 ? "билета" : "билетов"}
            {" · "}
            {formatShortDate(order.createdAt)}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="font-semibold text-[15px] text-slate-900">
            {order.totalAmount} {getCurrencySymbol(order.currency)}
          </span>
          <IconChevronRight className="w-5 h-5 text-slate-300" />
        </div>
      </button>
    );
  };

  return (
    <>
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-none">
        {filterTabs.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => {
              haptic("selection");
              setFilter(tab.value);
            }}
            className={`
              shrink-0 px-3 py-2 rounded-xl text-sm font-medium transition-colors
              ${filter === tab.value
                ? "bg-slate-900 text-white"
                : "bg-slate-100 text-slate-600 active:bg-slate-200"}
            `}
          >
            {tab.label}
            {tab.count > 0 && (
              <span
                className={
                  filter === tab.value
                    ? "text-white/80 ml-1"
                    : "text-slate-400 ml-1"
                }
              >
                ({tab.count})
              </span>
            )}
          </button>
        ))}
      </div>

      {isEmptyFiltered ? (
        <div className={`${CARD_CLASS} p-5 text-center`}>
          <p className="text-slate-500 text-sm">
            В этой категории пока нет заказов.
          </p>
          <button
            type="button"
            onClick={() => setFilter("all")}
            className="mt-3 text-sm font-medium text-slate-700 underline"
          >
            Показать все заказы
          </button>
        </div>
      ) : (
        <>
          {showActive && activeOrders.length > 0 && (
            <div className="mb-5">
              <h2 className="section-title">Активные</h2>
              <div className={`${CARD_CLASS} overflow-hidden`}>
                {activeOrders.map((order) => (
                  <OrderCard key={order.id} order={order} />
                ))}
              </div>
            </div>
          )}

          {showCompleted && completedOrders.length > 0 && (
            <div className="mb-5">
              <h2 className="section-title">Оплаченные</h2>
              <div className="rounded-xl bg-white border border-slate-200/60 overflow-hidden">
                {completedOrders.map((order) => (
                  <OrderCard key={order.id} order={order} />
                ))}
              </div>
            </div>
          )}

          {showArchive && otherOrders.length > 0 && (
            <div className="mb-5">
              <h2 className="section-title">Архив</h2>
              <div className={`${CARD_CLASS} overflow-hidden`}>
                {otherOrders.map((order) => (
                  <OrderCard key={order.id} order={order} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
